import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';

/**
 * ScanQualityReportSkeleton renders a loading placeholder that mirrors the
 * layout of ScanQualityReport. Shows a shimmer skeleton for:
 * - Page title and description
 * - DocumentOverviewCard (header + stats row)
 * - N ExtractedDocumentPanels (header + accordion sections)
 *
 * @param {Object} props
 * @param {number} [props.extractedDocCount] - Number of extracted document skeletons to show (default 2)
 */
const ScanQualityReportSkeleton = ({ extractedDocCount = 2 }) => {
  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      {/* Page title */}
      <Skeleton variant="text" width={280} height={36} sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width="70%" height={20} sx={{ mb: 3 }} />

      <Stack spacing={3}>
        {/* Document Overview Card skeleton */}
        <OverviewCardSkeleton />

        {/* Extracted Documents heading */}
        <Box>
          <Skeleton variant="text" width={200} height={28} sx={{ mb: 2 }} />

          <Stack spacing={2}>
            {Array.from({ length: extractedDocCount }, (_, i) => (
              <ExtractedDocumentSkeleton key={i} />
            ))}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

/** Mirrors DocumentOverviewCard layout */
const OverviewCardSkeleton = () => (
  <Card variant="outlined">
    <CardContent>
      {/* Header row: icon + title + status chip */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        <Skeleton variant="circular" width={36} height={36} />
        <Box sx={{ flexGrow: 1 }}>
          <Skeleton variant="text" width="50%" height={28} />
          <Skeleton variant="text" width="35%" height={18} />
        </Box>
        <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: 3 }} />
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Stats row */}
      <Box sx={{ display: 'flex', gap: 4 }}>
        {[1, 2, 3].map((i) => (
          <Box key={i}>
            <Skeleton variant="text" width={80} height={16} />
            <Skeleton variant="text" width={40} height={28} />
          </Box>
        ))}
      </Box>

      {/* Doc type chips */}
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Skeleton variant="rounded" width={120} height={28} sx={{ borderRadius: 1 }} />
      </Box>
    </CardContent>
  </Card>
);

/** Mirrors ExtractedDocumentPanel layout */
const ExtractedDocumentSkeleton = () => (
  <Card variant="outlined">
    <CardContent>
      {/* Panel header: display name + chips */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Skeleton variant="text" width={260} height={24} />
          </Box>
          <Skeleton variant="text" width="55%" height={18} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="rounded" width={90} height={24} sx={{ borderRadius: 3 }} />
          <Skeleton variant="rounded" width={72} height={24} sx={{ borderRadius: 3 }} />
        </Box>
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      {/* Accordion section skeletons */}
      {['Summary Scan', 'Deep Scan', 'Scan Comparison', 'Final Disposition'].map(
        (label, idx) => (
          <Box key={label}>
            {idx > 0 && <Divider sx={{ my: 1 }} />}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1,
                py: 0.75,
              }}
            >
              <Skeleton variant="text" width={120 + idx * 15} height={22} sx={{ flexGrow: 0 }} />
              <Box sx={{ flexGrow: 1 }} />
              <Skeleton variant="text" width={36} height={18} sx={{ mr: 1 }} />
              <Skeleton variant="circular" width={24} height={24} />
            </Box>
          </Box>
        )
      )}
    </CardContent>
  </Card>
);

ScanQualityReportSkeleton.propTypes = {
  extractedDocCount: PropTypes.number,
};

export default ScanQualityReportSkeleton;